import axios from 'axios';
import { fetchAuthSession } from 'aws-amplify/auth';

export interface DeviceMapping {
  serialNumber: string;
  qrIdentifier: string;
  orientation: 'portrait' | 'landscape';
  imageCreated: boolean;
  etchCompleted: boolean;
  createdAt?: string;
  createdBy?: string;
  updatedAt?: string;
  updatedBy?: string;
}

export interface EnvironmentConfig {
  name: string;
  displayName: string;
  apiEndpoint: string;
}

export interface EnvironmentsResponse {
  availableEnvironments: EnvironmentConfig[];
  userGroups: string[];
}

export class ApiService {
  private apiEndpoint: string;

  constructor(apiEndpoint: string) {
    this.apiEndpoint = apiEndpoint;
  }

  private async getHeaders(environment?: string) {
    try {
      const session = await fetchAuthSession();
      const token = session.tokens?.idToken?.toString();
      
      if (!token) {
        throw new Error('No authentication token available');
      }
      
      const headers: Record<string, string> = {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      };
      
      if (environment) {
        headers['X-Environment'] = environment;
      }
      
      return headers;
    } catch (error) {
      console.error('Error getting auth token:', error);
      throw new Error('Authentication required');
    }
  }

  async createMapping(
    serialNumber: string, 
    url: string, 
    orientation: 'portrait' | 'landscape',
    imageCreated?: boolean,
    etchCompleted?: boolean,
    environment?: string
  ): Promise<DeviceMapping> {
    const headers = await this.getHeaders(environment);
    
    const response = await axios.post(
      `${this.apiEndpoint}/mappings`,
      { 
        serialNumber, 
        url, 
        orientation,
        imageCreated: imageCreated ?? false,
        etchCompleted: etchCompleted ?? false
      },
      { headers }
    );
    
    return response.data;
  }

  async getMapping(serialNumber: string, environment?: string): Promise<DeviceMapping | null> {
    try {
      const headers = await this.getHeaders(environment);
      const response = await axios.get(
        `${this.apiEndpoint}/mappings/${serialNumber}`,
        { headers }
      );
      return response.data;
    } catch (error: any) {
      if (error.response?.status === 404) {
        return null;
      }
      throw error;
    }
  }

  async getMappingByQR(qrParam: string, environment?: string): Promise<DeviceMapping | null> {
    try {
      const headers = await this.getHeaders(environment);
      // URL encode the parameter since it might contain special characters (URLs)
      const encodedParam = encodeURIComponent(qrParam);
      const response = await axios.get(
        `${this.apiEndpoint}/mappings/qr/${encodedParam}`,
        { headers }
      );
      return response.data;
    } catch (error: any) {
      if (error.response?.status === 404) {
        return null;
      }
      throw error;
    }
  }

  async getAvailableEnvironments(): Promise<EnvironmentsResponse> {
    const headers = await this.getHeaders();
    const response = await axios.get(
      `${this.apiEndpoint}/environments`,
      { headers }
    );
    return response.data;
  }

  async generateSerial(environment?: string): Promise<{ serialNumber: string; environment: string }> {
    const headers = await this.getHeaders(environment);
    const response = await axios.get(
      `${this.apiEndpoint}/generate-serial`,
      { headers }
    );
    return response.data;
  }

  async generateUuid(environment?: string): Promise<{ uuid: string; url: string; environment: string }> {
    const headers = await this.getHeaders(environment);
    const response = await axios.get(
      `${this.apiEndpoint}/generate-uuid`,
      { headers }
    );
    return response.data;
  }

  async updateMapping(
    serialNumber: string,
    imageCreated: boolean,
    etchCompleted: boolean,
    environment?: string
  ): Promise<{ message: string; mapping: DeviceMapping }> {
    const headers = await this.getHeaders(environment);
    const response = await axios.put(
      `${this.apiEndpoint}/mappings/${encodeURIComponent(serialNumber)}`,
      { imageCreated, etchCompleted },
      { headers }
    );
    return response.data;
  }

  async getTemplate(type: 'portrait' | 'landscape', environment?: string): Promise<{
    templateType: string;
    contentType: string;
    data: string;
    size: number;
  }> {
    const headers = await this.getHeaders(environment);
    const response = await axios.get(
      `${this.apiEndpoint}/templates/${type}?format=png`,
      { headers }
    );
    return response.data;
  }

  async getTemplateHiRes(type: 'portrait' | 'landscape', environment?: string): Promise<{
    templateType: string;
    contentType: string;
    data: string;
    size: number;
  }> {
    const headers = await this.getHeaders(environment);
    const response = await axios.get(
      `${this.apiEndpoint}/templates/${type}?format=tiff`,
      { headers }
    );
    return response.data;
  }

  async generateTiffComposite(
    serialNumber: string,
    qrUrl: string,
    orientation: 'portrait' | 'landscape',
    environment?: string,
    qrCodeDataUrl?: string
  ): Promise<{ downloadUrl: string; filename: string; s3Key: string; size: string }> {
    const headers = await this.getHeaders(environment);
    const endpoint = this.apiEndpoint.endsWith('/') ? this.apiEndpoint.slice(0, -1) : this.apiEndpoint;
    const response = await axios.post(
      `${endpoint}/generate-tiff-composite`,
      {
        serialNumber,
        qrUrl,
        orientation,
        qrCodeDataUrl
      },
      { 
        headers,
        responseType: 'json'
      }
    );
    return response.data;
  }

  async refreshTiffDownload(
    s3Key: string,
    environment?: string
  ): Promise<{ downloadUrl: string; s3Key: string; refreshedAt: string }> {
    const headers = await this.getHeaders(environment);
    const endpoint = this.apiEndpoint.endsWith('/') ? this.apiEndpoint.slice(0, -1) : this.apiEndpoint;
    const response = await axios.post(
      `${endpoint}/refresh-tiff-download`,
      { s3Key },
      { headers }
    );
    return response.data;
  }
}